import React, { useCallback, useEffect, useState } from 'react';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import {
    Overlay,
    BannerContainer,
    CloseButton,
    IllustrationWrapper,
    Title,
    Description,
    ButtonRow,
    PrimaryButton,
    SecondaryButton,
} from './cookie-reconsent-banner.style';
import illustrationImg from './illustration-furniture.png';

const STORAGE_KEY = 'cookie_reconsent_dismissed_at';
const CMP_REPROMPT_KEY = 'cmp_reprompt';
const LOCAL_ID_KEY = 'cookie_reconsent_local_id';
const COOLING_PERIOD_MS = 1000 * 24 * 60 * 60 * 1000; // ~2.7 years

export function getOrCreateLocalId(): string {
    const existing = localStorage.getItem(LOCAL_ID_KEY);
    if (existing) return existing;

    const id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    localStorage.setItem(LOCAL_ID_KEY, id);
    return id;
}

type Variant = 'A' | 'B' | 'C';

interface VariantContent {
    title: string;
    description: string;
    primaryLabel: string;
    secondaryLabel: string;
}

const variantContentMap: Record<Variant, VariantContent> = {
    A: {
        title: 'Keep Gumtree free and useful',
        description:
            'Cookies help fund Gumtree through ads, improve site performance, and keep the site free for everyone.',
        primaryLabel: 'Support Gumtree',
        secondaryLabel: 'Continue without supporting',
    },
    B: {
        title: 'Help support your community',
        description:
            'Accepting cookies supports a free Gumtree and a community that saves money, cuts waste, and keeps goods in use for longer.',
        primaryLabel: 'Support a free Gumtree',
        secondaryLabel: 'Not now',
    },
    C: {
        title: 'See ads that matter to you',
        description:
            'Cookies help us show more relevant ads, so you spend less time scrolling and more time finding what you need.',
        primaryLabel: 'Enable this experience',
        secondaryLabel: 'Skip',
    },
};

declare global {
    interface Window {
        OneTrust?: {
            ToggleInfoDisplay: () => void;
            AllowAll: () => void;
            IsAlertBoxClosed?: () => boolean;
        };
        Optanon?: {
            IsAlertBoxClosed?: () => boolean;
        };
    }
}

function hasUserRejectedCookies(): boolean {
    const optanonCookie = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith('OptanonConsent='));

    if (!optanonCookie) return false;

    const cookieValue = decodeURIComponent(optanonCookie.split('=').slice(1).join('='));
    const groupsMatch = cookieValue
        .split('&')
        .map((pair) => pair.split('='))
        .find(([key]) => key === 'groups');

    if (!groupsMatch || groupsMatch.length < 2) return false;

    const groups = groupsMatch[1].split(',');
    const hasRejectedOptional = groups.some((group) => {
        const [id, value] = group.split(':');
        return id !== 'C0001' && value === '0';
    });

    return hasRejectedOptional;
}

function isCoolingPeriodOver(): boolean {
    const dismissedAt = localStorage.getItem(STORAGE_KEY);

    if (!dismissedAt) {
        const alertBoxClosed = document.cookie
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith('OptanonAlertBoxClosed='));

        if (alertBoxClosed) {
            const dateStr = decodeURIComponent(alertBoxClosed.split('=').slice(1).join('='));
            const closedDate = new Date(dateStr).getTime();
            if (!isNaN(closedDate)) {
                return Date.now() - closedDate >= COOLING_PERIOD_MS;
            }
        }
        return false;
    }

    return Date.now() - Number(dismissedAt) >= COOLING_PERIOD_MS;
}

interface CookieReconsentBannerProps {
    variant: Variant;
}

const CookieReconsentBanner = ({ variant }: CookieReconsentBannerProps) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!hasUserRejectedCookies()) return;
        if (!isCoolingPeriodOver()) return;

        setIsVisible(true);
        setTimeout(() => {
            trackGA4Event({
                event: 'reconsent_banner_impress',
                localId: getOrCreateLocalId(),
                privacySettingsVariation: variant,
                _explicitReconsentEvent: true,
            } as GA4.ReconsentBannerImpressEvent);
        }, 1000);
    }, [variant]);

    const trackClick = useCallback(
        (action: GA4.ReconsentBannerClickEvent['clickReconsentBanner']) => {
            trackGA4Event({
                event: 'reconsent_banner',
                localId: getOrCreateLocalId(),
                clickReconsentBanner: action,
                privacySettingsVariation: variant,
                _explicitReconsentEvent: true,
            } as GA4.ReconsentBannerClickEvent);
        },
        [variant]
    );

    const handleClose = useCallback(() => {
        setIsVisible(false);
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        localStorage.setItem(CMP_REPROMPT_KEY, `${variant}_decline`);
        trackClick('close');
    }, [variant, trackClick]);

    const handleSkip = useCallback(() => {
        setIsVisible(false);
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        localStorage.setItem(CMP_REPROMPT_KEY, `${variant}_decline`);
        trackClick('skip');
    }, [variant, trackClick]);

    const handleManageOptions = useCallback(() => {
        setIsVisible(false);
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        localStorage.setItem(CMP_REPROMPT_KEY, `${variant}_accept`);
        trackClick('manage options');

        sessionStorage.setItem('privacy_pref_source', 'reconsent_banner');

        if (window.OneTrust?.ToggleInfoDisplay) {
            window.OneTrust.ToggleInfoDisplay();
        }
    }, [variant, trackClick]);

    if (!isVisible) return null;

    const content = variantContentMap[variant];

    return (
        <Overlay data-testid="cookie-reconsent-banner">
            <BannerContainer>
                <CloseButton
                    type="button"
                    onClick={handleClose}
                    aria-label="Close"
                    data-testid="cookie-reconsent-close"
                >
                    &#x2715;
                </CloseButton>

                <IllustrationWrapper>
                    <img src={illustrationImg} alt="Gumtree illustration" />
                </IllustrationWrapper>

                <Title>{content.title}</Title>
                <Description>{content.description}</Description>

                <ButtonRow>
                    <SecondaryButton
                        type="button"
                        onClick={handleSkip}
                        data-testid="cookie-reconsent-dismiss"
                    >
                        {content.secondaryLabel}
                    </SecondaryButton>
                    <PrimaryButton
                        type="button"
                        onClick={handleManageOptions}
                        data-testid="cookie-reconsent-accept"
                    >
                        {content.primaryLabel}
                    </PrimaryButton>
                </ButtonRow>
            </BannerContainer>
        </Overlay>
    );
};

export default CookieReconsentBanner;
